/**
 * Marca y línea, escogidas de una lista.
 *
 * Escritas a mano el mismo carro entraba como «chevrolet», «Chevrolét» y
 * «CHEVROLET»: tres marcas distintas para la base y ningún reporte posible.
 *
 * La lista se puede escribir encima para buscar, y si lo escrito no está,
 * ofrece crearlo ahí mismo. Eso es deliberado: con el carro esperando en la
 * entrada, mandar al operario a otra pantalla a dar de alta una marca es la
 * forma segura de que escriba cualquier cosa —o de que no registre nada—. Lo
 * que se crea queda en el catálogo y la próxima vez ya sale en la lista.
 *
 * La línea cuelga de la marca porque sola no dice nada: hay un «3» de Mazda y
 * un «3» de BMW.
 */
import React, { useState } from 'react';
import { Autocomplete, TextField, Box, Typography, createFilterOptions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '../api';

const filtrar = createFilterOptions();

// Se guarda como lo guarda el backend, para que lo escrito y lo listado
// coincidan y no aparezca un duplicado que solo difiere en mayúsculas.
const titulo = (t) => (t || '').trim().split(/\s+/).map(
  (p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ');

export default function SelectorMarcaLinea({
  marcas, marca, linea, onChange, onCatalogo, disabled, size = 'small',
}) {
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState('');

  const marcaSel = marcas.find((m) => m.nombre === marca) || null;
  const lineas = marcaSel ? marcaSel.lineas : [];

  const crearMarca = async (nombre) => {
    setCreando(true); setError('');
    try {
      const { data } = await api.post('/marcas', { nombre: titulo(nombre) });
      await onCatalogo?.();
      onChange({ marca: data.nombre, linea: '' });
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo crear la marca');
    } finally { setCreando(false); }
  };

  const crearLinea = async (nombre) => {
    if (!marcaSel) { setError('Escoge primero la marca.'); return; }
    setCreando(true); setError('');
    try {
      const { data } = await api.post('/lineas',
        { marca_id: marcaSel.id, nombre: titulo(nombre) });
      await onCatalogo?.();
      onChange({ marca: marcaSel.nombre, linea: data.nombre });
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo crear la línea');
    } finally { setCreando(false); }
  };

  // Añade la opción «Crear …» solo cuando lo escrito no existe ya.
  const conCrear = (opciones, estado, existentes) => {
    const filtradas = filtrar(opciones, estado);
    const t = titulo(estado.inputValue);
    if (t && !existentes.some((x) => x.toLowerCase() === t.toLowerCase())) {
      filtradas.push({ _crear: true, nombre: t });
    }
    return filtradas;
  };

  const pintar = (props, o) => (
    <li {...props} key={o._crear ? '__crear' : o.id}>
      {o._crear ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'primary.main' }}>
          <AddIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 13.5 }}>
            Crear <strong>{o.nombre}</strong>
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'baseline' }}>
          <Typography sx={{ fontSize: 13.5, flexGrow: 1 }}>{o.nombre}</Typography>
          {o.vehiculos > 0 && (
            <Typography sx={{ fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap' }}>
              {o.vehiculos} vehículo{o.vehiculos === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
      )}
    </li>
  );

  return (
    <>
      <Autocomplete
        freeSolo selectOnFocus handleHomeEndKeys fullWidth size={size}
        disabled={disabled || creando}
        options={marcas}
        value={marcaSel}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.nombre)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        filterOptions={(o, e) => conCrear(o, e, marcas.map((m) => m.nombre))}
        renderOption={pintar}
        onChange={(_, v) => {
          if (!v) { onChange({ marca: '', linea: '' }); return; }
          if (typeof v === 'string') { crearMarca(v); return; }
          if (v._crear) { crearMarca(v.nombre); return; }
          // Cambiar de marca invalida la línea: un Duster no es de Kia.
          onChange({ marca: v.nombre, linea: '' });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Marca"
            error={Boolean(error)} helperText={error || undefined} />
        )} />
      <Autocomplete
        freeSolo selectOnFocus handleHomeEndKeys fullWidth size={size}
        disabled={disabled || creando || !marcaSel}
        options={lineas}
        value={lineas.find((l) => l.nombre === linea)
          || (linea ? { id: '_suelta', nombre: linea } : null)}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.nombre)}
        isOptionEqualToValue={(o, v) => o.nombre === v.nombre}
        filterOptions={(o, e) => conCrear(o, e, lineas.map((l) => l.nombre))}
        renderOption={pintar}
        onChange={(_, v) => {
          if (!v) { onChange({ marca, linea: '' }); return; }
          if (typeof v === 'string') { crearLinea(v); return; }
          if (v._crear) { crearLinea(v.nombre); return; }
          onChange({ marca, linea: v.nombre });
        }}
        renderInput={(params) => (
          <TextField {...params} label="Línea"
            helperText={marcaSel ? undefined : 'Escoge primero la marca'} />
        )} />
    </>
  );
}
